import React, { useState, useRef, useEffect } from 'react';
import { InoRecycleListProps } from './InoRecycleList.types';
import { useKeydown } from '../../hooks/useKeydown';
import '../../styles/InoRecycleList.css';

export const InoRecycleList = <T,>({
  items,
  itemHeight,
  visibleItemsCount,
  buffer = 0,
  scrollIndex = 0,
  isActive = false,
  infinite = false,
  classNames = '',
  renderItem,
  onActiveChange,
  onUp,
  onDown,
  onLeft,
  onRight,
  onOk,
}: InoRecycleListProps<T>) => {
  const [scrollTop, setScrollTop] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const height = visibleItemsCount * itemHeight;

  // Calculate visible range with buffer
  const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - buffer);
  const endIndex = Math.min(
    items.length,
    Math.ceil((scrollTop + height) / itemHeight) + buffer
  );

  // Calculate padding to maintain scroll height
  const topPadding = startIndex * itemHeight;
  const bottomPadding = (items.length - endIndex) * itemHeight;

  useEffect(() => {
    if (isActive && onActiveChange) {
      onActiveChange(activeIndex);
    }
  }, [isActive, activeIndex, onActiveChange]);

  useEffect(() => {
    if (scrollIndex > 0 && containerRef.current) {
      const scrollPosition = scrollIndex * itemHeight;
      containerRef.current.scrollTop = scrollPosition;
      setScrollTop(scrollPosition);
      setActiveIndex(scrollIndex);
    }
  }, [scrollIndex, itemHeight]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  };

  const scrollToIndex = (index: number) => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const itemTop = index * itemHeight;
    const itemBottom = itemTop + itemHeight;
    const scrollBottom = scrollTop + height;

    if (itemTop < scrollTop) {
      container.scrollTop = itemTop;
    } else if (itemBottom > scrollBottom) {
      container.scrollTop = itemBottom - height;
    }
  };

  const handleNavigation = (direction: 'up' | 'down') => {
    if (!isActive) return;

    setActiveIndex((prev) => {
      const nextIndex = direction === 'up' ? prev - 1 : prev + 1;

      if (infinite) {
        if (nextIndex < 0) return items.length - 1;
        if (nextIndex >= items.length) return 0;
      } else {
        if (nextIndex < 0 || nextIndex >= items.length) return prev;
      }

      scrollToIndex(nextIndex);
      return nextIndex;
    });
  };

  useKeydown({
    isActive,
    up: () => {
      if (activeIndex === 0 && !infinite) {
        onUp?.();
      } else {
        handleNavigation('up');
      }
    },
    down: () => {
      if (activeIndex === items.length - 1 && !infinite) {
        onDown?.();
      } else {
        handleNavigation('down');
      }
    },
    left: onLeft,
    right: onRight,
    ok: onOk,
  });

  return (
    <div
      ref={containerRef}
      className={`ino-recycle-list ${classNames}`}
      style={{ height }}
      onScroll={handleScroll}
    >
      <div style={{ height: topPadding }} />
      {items.slice(startIndex, endIndex).map((item, index) => (
        <div
          key={startIndex + index}
          className="ino-recycle-list__item"
          style={{ height: itemHeight }}
        >
          {renderItem(
            item,
            startIndex + index,
            startIndex + index === activeIndex
          )}
        </div>
      ))}
      <div style={{ height: bottomPadding }} />
    </div>
  );
};
